import { useEffect, useMemo, useState } from 'react';
import { Activity, MonitorSmartphone, Smartphone, RefreshCw } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { themeQuartz, colorSchemeLightCold } from 'ag-grid-community';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { fetchActiveSessions, type ActiveSessionEntry } from '@/api/monitoring';
import { useErrorToast } from '@/lib/notify';

const gridTheme = themeQuartz.withPart(colorSchemeLightCold).withParams({
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 11.5,
  rowHeight: 32,
  headerHeight: 32,
  borderRadius: 6,
  headerBackgroundColor: '#f8fafc',
  borderColor: '#e2e8f0',
  oddRowBackgroundColor: '#f8fafc',
});

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('tr-TR');
}

function fmtAgo(value: string): string {
  const diffMs = Date.now() - new Date(value).getTime();
  const diffSec = Math.max(0, Math.floor(diffMs / 1000));
  if (diffSec < 60) return `${diffSec} sn önce`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} dk önce`;
  return `${Math.floor(diffSec / 3600)} sa önce`;
}

function fmtMs(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(2)} sn`;
  return `${value} ms`;
}

function clientTypeBadge(type: ActiveSessionEntry['clientType']) {
  if (type === 'mobile') {
    return { label: 'Mobil', className: 'bg-amber-100 text-amber-700', icon: <Smartphone className="h-3.5 w-3.5" /> };
  }
  if (type === 'web') {
    return { label: 'Web', className: 'bg-blue-100 text-blue-700', icon: <MonitorSmartphone className="h-3.5 w-3.5" /> };
  }
  return { label: 'Bilinmiyor', className: 'bg-slate-100 text-slate-600', icon: <Activity className="h-3.5 w-3.5" /> };
}

function freshnessTone(ttlSeconds: number): string {
  if (ttlSeconds >= 240) return 'text-emerald-600';
  if (ttlSeconds >= 120) return 'text-amber-600';
  return 'text-orange-600';
}

export default function SessionsTab() {
  const [rows, setRows] = useState<ActiveSessionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let closed = false;

    async function load() {
      try {
        setError('');
        const data = await fetchActiveSessions();
        if (!closed) setRows(data);
      } catch {
        if (!closed) setError('Oturumlar yüklenemedi.');
      } finally {
        if (!closed) setLoading(false);
      }
    }

    setLoading(true);
    void load();

    const interval = window.setInterval(() => {
      setRefreshTick((value) => value + 1);
    }, 15000);

    return () => {
      closed = true;
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (refreshTick === 0) return;
    let closed = false;

    async function reload() {
      try {
        const data = await fetchActiveSessions();
        if (!closed) setRows(data);
      } catch {
        if (!closed) setError('Oturumlar yenilenemedi.');
      }
    }

    void reload();

    return () => {
      closed = true;
    };
  }, [refreshTick]);

  const activeCount = rows.length;
  const mobileCount = rows.filter((item) => item.clientType === 'mobile').length;
  const webCount = rows.filter((item) => item.clientType === 'web').length;
  const errorCount = rows.reduce((sum, item) => sum + item.errorCount, 0);

  const colDefs = useMemo<ColDef<ActiveSessionEntry>[]>(() => [
    {
      headerName: 'Kullanıcı',
      field: 'email',
      tooltipValueGetter: ({ data }) => [data?.username, data?.email].filter(Boolean).join(' · '),
      flex: 1.4,
      minWidth: 190,
      valueGetter: ({ data }) => data ? (data.username || data.email || `#${data.userId}`) : '',
      cellRenderer: ({ data }: ICellRendererParams<ActiveSessionEntry>) => {
        if (!data) return null;
        return (
          <div className="flex h-full min-w-0 items-center gap-2 overflow-hidden">
            <span className="truncate text-[11.5px] font-semibold text-slate-800">{data.username || `Kullanıcı #${data.userId}`}</span>
            <span className="truncate text-[11px] text-slate-500">{data.email || '—'}</span>
          </div>
        );
      },
    },
    {
      headerName: 'Kaynak',
      field: 'source',
      width: 150,
      valueGetter: ({ data }) => data?.source === 'investor' ? 'Yatırımcı Paneli' : 'Ana Uygulama',
    },
    {
      headerName: 'Yatırımcı',
      field: 'investorName',
      minWidth: 200,
      flex: 1,
      valueFormatter: ({ value }) => value || '—',
    },
    {
      headerName: 'Kanal',
      field: 'clientType',
      width: 110,
      cellRenderer: ({ data }: ICellRendererParams<ActiveSessionEntry>) => {
        if (!data) return null;
        const badge = clientTypeBadge(data.clientType);
        return (
          <div className="flex h-full items-center">
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.className}`}>
              {badge.icon}
              {badge.label}
            </span>
          </div>
        );
      },
    },
    {
      headerName: 'Cihaz / Tarayıcı',
      field: 'deviceLabel',
      tooltipValueGetter: ({ data }) => [data?.deviceLabel, data?.browserLabel, data?.osLabel].filter(Boolean).join(' · '),
      minWidth: 180,
      flex: 1.1,
      cellRenderer: ({ data }: ICellRendererParams<ActiveSessionEntry>) => {
        if (!data) return null;
        return (
          <div className="flex h-full min-w-0 items-center gap-2 overflow-hidden">
            <span className="truncate text-[11.5px] font-medium text-slate-800">{data.deviceLabel || '—'}</span>
            <span className="truncate text-[11px] text-slate-500">
              {[data.browserLabel, data.osLabel].filter(Boolean).join(' • ') || '—'}
            </span>
          </div>
        );
      },
    },
    {
      headerName: 'Giriş',
      field: 'loginAt',
      minWidth: 145,
      valueFormatter: ({ value }) => fmtDate(String(value ?? '')),
    },
    {
      headerName: 'Son İstek',
      field: 'lastSeenAt',
      minWidth: 160,
      cellRenderer: ({ data }: ICellRendererParams<ActiveSessionEntry>) => {
        if (!data) return null;
        return (
          <div className="flex h-full min-w-0 items-center gap-2 overflow-hidden">
            <span className="truncate text-[11.5px] text-slate-700">{fmtDate(data.lastSeenAt)}</span>
            <span className={`text-[11px] font-medium ${freshnessTone(data.ttlSeconds)}`}>{fmtAgo(data.lastSeenAt)}</span>
          </div>
        );
      },
    },
    {
      headerName: 'İstek',
      field: 'requestCount',
      width: 82,
      cellClass: 'font-mono truncate text-[11.5px] text-slate-700',
    },
    {
      headerName: 'Hata',
      field: 'errorCount',
      width: 82,
      cellRenderer: ({ value }: ICellRendererParams<ActiveSessionEntry>) => {
        const count = Number(value ?? 0);
        const cls = count > 0 ? 'text-rose-600' : 'text-emerald-600';
        return <span className={`font-mono text-xs font-semibold ${cls}`}>{count}</span>;
      },
    },
    {
      headerName: 'Son Endpoint',
      field: 'lastPath',
      flex: 1.25,
      minWidth: 180,
      valueFormatter: ({ value, data }) => {
        const path = value ? String(value) : '—';
        const method = data?.lastMethod ? `${data.lastMethod} ` : '';
        return `${method}${path}`;
      },
    },
    {
      headerName: 'En Uzun Çağrı',
      field: 'longestRequestMs',
      minWidth: 180,
      flex: 1,
      cellRenderer: ({ data }: ICellRendererParams<ActiveSessionEntry>) => {
        if (!data || !data.longestRequestPath) return <span className="text-xs text-slate-400">—</span>;
        return (
          <div className="flex h-full min-w-0 items-center gap-2 overflow-hidden">
            <span className="truncate text-[11.5px] text-slate-700">{data.longestRequestPath}</span>
            <span className="text-[11px] font-semibold text-indigo-600">{fmtMs(data.longestRequestMs)}</span>
          </div>
        );
      },
    },
    {
      headerName: 'Son Hata',
      field: 'lastErrorPath',
      flex: 1,
      minWidth: 170,
      cellRenderer: ({ data }: ICellRendererParams<ActiveSessionEntry>) => {
        if (!data?.lastErrorPath) return <span className="text-xs text-slate-400">—</span>;
        return (
          <div className="flex h-full min-w-0 items-center gap-2 overflow-hidden">
            <span className="truncate text-[11.5px] text-rose-700">{data.lastErrorPath}</span>
            <span className="truncate text-[11px] text-slate-500">{fmtDate(data.lastErrorAt)}</span>
          </div>
        );
      },
    },
    {
      headerName: 'IP',
      field: 'ip',
      minWidth: 110,
      cellClass: 'font-mono text-xs text-slate-600',
      valueFormatter: ({ value }) => value || '—',
    },
  ], []);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs text-slate-500">Aktif Oturum</p>
          <p className="mt-1 text-2xl font-semibold text-slate-900">{activeCount}</p>
        </div>
        <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4">
          <p className="text-xs text-slate-500">Web</p>
          <p className="mt-1 text-2xl font-semibold text-blue-700">{webCount}</p>
        </div>
        <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-4">
          <p className="text-xs text-slate-500">Mobil</p>
          <p className="mt-1 text-2xl font-semibold text-amber-700">{mobileCount}</p>
        </div>
        <div className="rounded-xl border border-rose-100 bg-rose-50/60 p-4">
          <p className="text-xs text-slate-500">Toplam Hata</p>
          <p className="mt-1 text-2xl font-semibold text-rose-700">{errorCount}</p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">30 dakika boyunca istek gelmeyen oturumlar otomatik düşer.</div>
        <button
          type="button"
          onClick={() => setRefreshTick((value) => value + 1)}
          className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
        >
          <RefreshCw className="h-4 w-4" />
          Yenile
        </button>
      </div>


      <div className="min-h-[360px] flex-1 w-full overflow-hidden rounded-md border border-slate-200 bg-white">
        <AgGridReact<ActiveSessionEntry>
          modules={[AllEnterpriseModule]}
          theme={gridTheme}
          maintainColumnOrder
          pagination
          paginationPageSize={100}
          paginationPageSizeSelector={[25, 50, 100]}
          sideBar={{ toolPanels: [
            { id: 'columns', labelDefault: 'Sütunlar', labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' },
            { id: 'filters', labelDefault: 'Filtreler', labelKey: 'filters', iconKey: 'filter', toolPanel: 'agFiltersToolPanel' },
          ] }}
          statusBar={{ statusPanels: [{ statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'right' }] }}
          rowData={rows}
          getRowId={({ data }) => data.sessionId}
          columnDefs={colDefs}
          animateRows
          defaultColDef={{
            sortable: true,
            filter: true,
            floatingFilter: false,
            wrapHeaderText: true,
            autoHeaderHeight: true,
            suppressHeaderMenuButton: false,
            enablePivot: true,
            enableRowGroup: true,
            tooltipValueGetter: ({ valueFormatted, value }) => String(valueFormatted ?? value ?? ''),
            resizable: true,
          }}
          loading={loading}
          overlayNoRowsTemplate={'<span class="text-sm text-slate-400">Aktif oturum bulunamadı.</span>'}
        />
      </div>
    </div>
  );
}
